import { useEffect, useState } from "react";
import { Avatar } from "@mantine/core";
import { prefix, getAuthHeaders } from "../../../Boilerplate/api";

// Process-wide cache so a candidate's photo is fetched ONCE and the SAME object URL is shared by
// every <CandidateAvatar> on the page — the feed renders the same person across many rows, and
// without this each instance fired its own concurrent fetch and minted its own blob: URL (different
// src each → no reuse, no caching). Keyed by photo blob id (the content), so a re-enriched photo
// (new blob id) refetches. Object URLs live for the session — deliberately not revoked per-unmount,
// since sibling rows may still reference them; the set is bounded by distinct photographed contacts.
const photoUrlCache = new Map(); // blobId -> object URL
const photoInflight = new Map(); // blobId -> Promise<string|null>

function loadPhoto(candidateId, photoBlobId) {
  if (photoUrlCache.has(photoBlobId)) return Promise.resolve(photoUrlCache.get(photoBlobId));
  if (photoInflight.has(photoBlobId)) return photoInflight.get(photoBlobId);
  // ?v=<blobId> makes the URL content-addressed so the browser's HTTP cache survives reloads and
  // busts correctly when the photo changes (the endpoint returns the current photo regardless of v).
  const p = fetch(`${prefix}/candidates/${candidateId}/photo?v=${encodeURIComponent(photoBlobId)}`,
    { headers: getAuthHeaders() })
    .then((r) => (r.ok ? r.blob() : null))
    .then((b) => {
      const url = b ? URL.createObjectURL(b) : null;
      if (url) photoUrlCache.set(photoBlobId, url);
      return url;
    })
    .catch(() => null)
    .finally(() => photoInflight.delete(photoBlobId));
  photoInflight.set(photoBlobId, p);
  return p;
}

/**
 * Fetches the candidate's photo with auth headers and returns a shared object URL for
 * <Avatar src={...}>. Returns null while loading or on missing/failed fetch — Mantine Avatar then
 * falls through to the children (initials). Deduped + cached across the whole page (see above).
 */
export function useAuthedPhoto(candidateId, photoBlobId) {
  const [url, setUrl] = useState(() => (photoBlobId ? photoUrlCache.get(photoBlobId) ?? null : null));
  useEffect(() => {
    if (!candidateId || !photoBlobId) { setUrl(null); return undefined; }
    const cached = photoUrlCache.get(photoBlobId);
    if (cached) { setUrl(cached); return undefined; }
    let cancelled = false;
    loadPhoto(candidateId, photoBlobId).then((u) => { if (!cancelled) setUrl(u); });
    return () => { cancelled = true; };
  }, [candidateId, photoBlobId]);
  return url;
}

export function CandidateAvatar({ candidate, ...avatarProps }) {
  const photoUrl = useAuthedPhoto(candidate.id, candidate.photoBlobId);
  return (
    <Avatar src={photoUrl} {...avatarProps}>
      {candidate.name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
    </Avatar>
  );
}
